import Exchange from '@ui/features/Exchange';
import { useTranslation } from 'next-i18next';
import { Flex, HStack, VStack, Box } from 'styled-system/jsx';
import { Text } from '@ui/lib/park/ui/text';
import { useUserRedeemRequests } from './executors/hooks/useUserRedeemRequests';
import { useState } from 'react';
import { CommonPopupModal } from '@ui/components/CommonPopupModal/CommonPopupModal';
import { Button } from '@ui/lib/park/ui/button';
import { Badge } from '@ui/lib/park/ui/badge';
import { formatDate } from '@ui/utils/formatDate';
import { formatBigValue } from '@ui/lib/bignumber/bignumber';
import { useStandardClaim } from './executors/hooks/useStandardClaim';
import { useTokenDecimals, useErc20Symbol } from '@ui/hooks/useErc20Read';
import { Address } from 'viem';
import { CheckmarkCircle, Info } from '@ui/components/icons';
import { Icon } from '@ui/lib/park/ui/icon';

import { RedeemFees } from './RedeemFees';
import { DailyRedeemLimit } from './DailyRedeemLimit';
import { useDailyRedeemLimit } from './useDailyRedeemLimit';

import { useMonadEpoch, EPOCH_DURATION_MS } from '@ui/hooks/useMonadEpoch';
import { HLine } from '@ui/components/Line';

const RequestItem = ({
  request,
  index,
  onClaimSuccess,
}: {
  request: any;
  index: number;
  onClaimSuccess?: () => void;
}) => {
  const { t } = useTranslation();
  const { mutate: claim, isPending } = useStandardClaim({
    onReceiptSettled: () => onClaimSuccess?.(),
  });
  const { data: decimals } = useTokenDecimals({ tokenAddress: request.reserveToken });
  const { data: symbol } = useErc20Symbol({ tokenAddress: request.reserveToken });
  const { data: epochInfo } = useMonadEpoch();

  const isClaimable = request.currentEpoch >= request.completedEpoch;
  
  // Calculate completion time based on epochs
  let completionTime = new Date();
  if (epochInfo) {
    const epochsRemaining = Number(request.completedEpoch) - Number(epochInfo.epoch);
    if (epochsRemaining > 0) {
      // Time remaining = (epochs remaining * duration) - time elapsed in current epoch
      const timeElapsedInCurrentEpoch = Date.now() - epochInfo.startTimeMs;
      // If epochsRemaining is 1, we just need to finish the current epoch
      // If epochsRemaining is 2, we need to finish current + 1 full epoch
      const timeRemainingMs = (epochsRemaining * EPOCH_DURATION_MS) - timeElapsedInCurrentEpoch;
      completionTime = new Date(Date.now() + Math.max(0, timeRemainingMs));
    } else {
      // Already completed, just show it as past
      completionTime = new Date(); 
    }
  } else {
    // Fallback estimate: timestamp + 1 day
    completionTime = new Date(Number(request.timestamp) * 1000 + 86400000);
  }

  return (
    <Flex 
      justifyContent="space-between" 
      alignItems="center"
      width="full" 
      bg="black.lv2" 
      p={4} 
      borderRadius="lg"
      borderWidth="1px"
      borderColor="greyWhite.outline"
    >
      <VStack alignItems="flex-start" gap={2}>
        <HStack gap={2}>
          <Text color="white" fontWeight="medium" fontSize="16px">
            {formatBigValue(request.amount, { decimals: decimals || 18 })} MON
          </Text>
          {isClaimable ? (
            <Badge variant="solid" colorPalette="green" size="sm">
              <HStack gap={1}>
                <Icon boxSize="3">
                  <CheckmarkCircle />
                </Icon>
                {t("Ready")}
              </HStack>
            </Badge>
          ) : (
            <Badge variant="outline" colorPalette="gray" size="sm">
              <HStack gap={1}>
                <Icon boxSize="3">
                  <Info />
                </Icon>
                {t("Pending")}
              </HStack>
            </Badge>
          )}
        </HStack>
        
        <Text color="greyWhite.grey" fontSize="12px">
          {t("Est. Completion")}: {formatDate(completionTime)}
        </Text>
      </VStack>
      
      <Button 
        size="small" 
        variant={isClaimable ? "primary" : "outline"}
        disabled={!isClaimable || isPending} 
        onClick={() => claim(BigInt(index))}
        minW="80px"
      >
        {isPending ? t("Claiming...") : t("Claim")}
      </Button>
    </Flex>
  );
};

export const StandardRedeem = () => {
  const { t } = useTranslation();
  const { data: requests, refetch: refetchRequests } = useUserRedeemRequests();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const { totalLimit, remaining, nextResetTime, isExceeded, status } = useDailyRedeemLimit();

  const pendingCount = requests?.length || 0;

  return (
    <>
      <VStack w="full" gap={1}>
        <Exchange.Input />
        <DailyRedeemLimit 
          totalLimit={totalLimit}
          remaining={remaining}
          nextResetTime={nextResetTime}
          isExceeded={isExceeded}
          status={status}
        />
        <HLine w="full" />
        <Exchange.Redeem.TextOutput />
        <RedeemFees />
        <Flex width="full" justifyContent="space-between" alignItems="center" py={1}>
          <Text color="greyWhite.white" fontWeight="light" fontSize="14px">
            {t("common.Waiting time:")}
          </Text>
          <HStack gap={1}>
            <Text fontSize="14px" fontWeight="normal" color="white">
              ~1 DAY
            </Text>
          </HStack>
        </Flex>
        
        {pendingCount > 0 && (
          <Box pt={2} w="full">
            <Button 
              variant="outline" 
              w="full" 
              size="small"
              onClick={() => setIsPopupOpen(true)}
              color="accent.text"
              borderColor="accent.text"
              _hover={{ bg: "accent.text", color: "black" }}
            >
              <HStack gap={2}>
                <Icon boxSize="3.5">
                  <Info />
                </Icon>
                <Text fontSize="13px" color="inherit">
                  {t("View")} {pendingCount} {t("Pending Requests")}
                </Text>
              </HStack>
            </Button>
          </Box>
        )}
      </VStack>
      <Exchange.ExecuteButton label="Redeem" disabled={isExceeded} />
      
      <CommonPopupModal
        open={isPopupOpen}
        onOpenChange={(e) => setIsPopupOpen(e.open)}
        title={t("Redeem Requests")}
        subTitle={""}
        content={
          <VStack gap={3} width="full" maxH="60vh" overflowY="auto" py={1}>
            {requests?.length === 0 ? (
              <Flex py={8} justifyContent="center" width="full">
                <Text color="greyWhite.grey">{t("No pending requests")}</Text>
              </Flex>
            ) : (
              requests?.map((req, index) => (
                <RequestItem
                  key={req.requestId.toString()}
                  request={req}
                  index={index}
                  onClaimSuccess={refetchRequests}
                />
              ))
            )}
          </VStack>
        }
      />
    </>
  );
};
